import React, {useState} from 'react';
import styled from 'styled-components';
import {Form, Formik, FormikHelpers} from 'formik';
import {useTranslations} from "use-intl";

import {Nullable} from '@tager/web-core';

import {TextInputFormik} from '@/components/TextInput';
import Button from '@/components/Button';
import {areAllErrorsVisible, validatePhone,} from '@/components/TextInput/formikHelpers/form.helpers';
import {OrderFormPayload, sendOrderForm} from '@/services/requests';
import UseModalsOpen from '@/hooks/useModalsOpen';
import {ProductCardFormType} from '@/components/ProductCard/ProductCardForm';
import {Checkbox} from "@/components/Checkbox";
import {useSettingItem} from "@/hooks";
import {colors} from "@/constants/theme";
import {TFunction} from "@/typings/common";
import {getCurrentProject} from "@/utils/common";

const hasFrontendValidationErrors = (t:TFunction, values: OrderFormPayload) =>
  !!Object.keys(validate(t, values)).length;

function validate(t: TFunction, {phone, name}: OrderFormPayload) {
  const errors: Partial<OrderFormPayload> = {};
  if (!validatePhone(phone)) {
    errors.phone = t('forms.phone_error');
  }
  if (!phone) {
    errors.phone = t('forms.required');
  }
  if (!name) {
    errors.name = t('forms.required');
  }
  return errors;
}

type Props = {
  product: Nullable<ProductCardFormType & { id: number }>;
  setFormClosed: () => void;
};

function OrderFormRequest({setFormClosed, product}: Props) {
  const {closeModalState} = UseModalsOpen();
  const t = useTranslations();

  const agreementText = useSettingItem('ORDER_FORM_AGREEMENT_TEXT');

  const [agreementChecked, setAgreementChecked] = useState<boolean>(false);

  function handleSubmit(
    values: OrderFormPayload,
    formikHelpers: FormikHelpers<OrderFormPayload>
  ) {
    return sendOrderForm(values)
      .then(() => {
        setFormClosed();
        closeModalState();
        formikHelpers.setSubmitting(true);
      })
      .catch(() => {
        formikHelpers.setErrors({
          phone: t('forms.unknown_error'),
        });
        formikHelpers.setStatus({
          hasBackendError: true,
        });
      })
      .finally(() => {
        formikHelpers.setSubmitting(false);
      });
  }

  return (
    <Component>
      <Formik<OrderFormPayload>
        initialValues={{
          productId: product?.id as number,
          option: product?.optionName
            ? product?.optionName + ': ' + product?.optionValue
            : '',
          name: '',
          phone: '',
        }}
        onSubmit={handleSubmit}
        validate={fields => validate(t, fields)}
      >
        {({isSubmitting, isValid, values, dirty, ...props}) => (
          <Form>
            {product?.notInStock ? <NotAvailable>
              {t('forms.no_item')}
            </NotAvailable> : null}
            <FormRow>
              <TextInputFormik
                name="name"
                icon="profile"
                placeholder={t('forms.name')}
                autoComplete="off"
                invalid={false}
              />
            </FormRow>
            <FormRow>
              <TextInputFormik
                name="city"
                placeholder={t('forms.city')}
                autoComplete="off"
                invalid={false}
              />
            </FormRow>
            <FormRow>
              <TextInputFormik
                name="phone"
                icon="phone"
                placeholder={t('forms.phone')}
                autoComplete="off"
                invalid={false}
              />
            </FormRow>
            {getCurrentProject() !== 'dveri' && <AgreementRow>
              <Checkbox checked={agreementChecked} onChange={() => setAgreementChecked(!agreementChecked)}
                        label={agreementText || ''}/>
            </AgreementRow>}
            <SubmitRow>
              <Button
                type="submit"
                variant="contained-primary-w100-f13"
                disabled={
                  !agreementChecked || !(isValid || !hasFrontendValidationErrors(t, values)) &&
                  areAllErrorsVisible(props.errors, props.touched)
                }
                children={t('forms.send')}
                loading={isSubmitting}
              />
            </SubmitRow>
          </Form>
        )}
      </Formik>
    </Component>
  );
}

export default OrderFormRequest;

const Component = styled.div`
  padding-bottom: 30px;
`;
const FormRow = styled.div`
  margin-bottom: 20px;
`;

const Text = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 20px;
`;


const AgreementRow = styled.div`
  margin-top: 20px;
  min-height: 20px;
`

const SubmitRow = styled.div`
  margin-top: 20px;
`

const NotAvailable = styled.div`
  background: ${colors.primary};
  color: #fff;
  padding: 10px 30px;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  margin-top: 20px;
  font-weight: bold;
  margin-bottom: 20px;
`
